import React from 'react';

import { DefaultNodeTypes } from '@payloadcms/richtext-lexical';
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical';
import {
  JSXConvertersFunction,
  RichText,
} from '@payloadcms/richtext-lexical/react';
import { AutoPriceConverterBlock } from '~/payload/blocks/AutoPriceConverter/Component';
import { ImageCarouselBlock } from '~/payload/blocks/ImageCarousel/Component';
import clsx from 'clsx';

import AccordionBlock from './Accordion/Component';
import CenterContentBlock from './CenterContent/Component';
import { CityPlannerBlock } from './CityPlanner/Component';
import CodeBlock from './Code/CodeBlock';
import RehypePrettyCodeCopy from './Code/rehype-pretty-code/rehypePrettyCodeCopy';
import { ContentBlock } from './Content/Component';
import { FormBlock } from './Form/Component';
import HtmlBlock from './Html/Component';
import { ImageBlock } from './Image/Component';
import InfoBlock from './InfoBlock/Component';
import { InlineImageBlock } from './InlineImage/Component';
import { LinkBlock } from './Link/Component';
import { MagicTextBlock } from './MagicText/Component';
import RoleBlock from './Role/Component';
import { SteamGameBlock } from './SteamGame/Component';
import StyleBlock from './Style/Component';
import VideoBlock from './Video/Component';
import VideoUrlBlock from './VideoUrl/Component';

type NodeTypes = DefaultNodeTypes;

const jsxConverters: JSXConvertersFunction<NodeTypes> = ({
  defaultConverters,
}) => {
  const { link, relationship, ...rest } = defaultConverters;
  return {
    inlineBlocks: {
      autoPriceConverter: ({ node }: { node: any }) => (
        <AutoPriceConverterBlock {...node.fields} />
      ),
      inlineImage: ({ node }: { node: any }) => (
        <InlineImageBlock {...node.fields} />
      ),
    },
    blocks: {
      imageCarousel: ({ node }: { node: any }) => (
        <ImageCarouselBlock {...node.fields} />
      ),
      content: ({ node }: { node: any }) => <ContentBlock {...node.fields} />,
      form: ({ node }: { node: any }) => <FormBlock {...node.fields} />,
      code: ({ node }: { node: any }) => <CodeBlock node={node}></CodeBlock>,
      image: ({ node }: { node: any }) => <ImageBlock {...node.fields} />,
      accordion: ({ node }: { node: any }) => {
        return <AccordionBlock {...node.fields}></AccordionBlock>;
      },
      info: ({ node }: { node: any }) => {
        return <InfoBlock {...node.fields}></InfoBlock>;
      },
      videoUrl: ({ node }: { node: any }) => {
        return <VideoUrlBlock {...node.fields}></VideoUrlBlock>;
      },
      video: ({ node }: { node: any }) => {
        return <VideoBlock {...node.fields}></VideoBlock>;
      },
      html: ({ node }: { node: any }) => {
        return <HtmlBlock {...node.fields}></HtmlBlock>;
      },
      style: ({ node }: { node: any }) => {
        return <StyleBlock {...node.fields}></StyleBlock>;
      },
      steamGame: ({ node }: { node: any }) => {
        return <SteamGameBlock {...node.fields}></SteamGameBlock>;
      },
      role: ({ node }: { node: any }) => {
        return <RoleBlock {...node.fields}></RoleBlock>;
      },
      magicText: ({ node }: { node: any }) => {
        return <MagicTextBlock {...node.fields}></MagicTextBlock>;
      },
      centerContent: ({ node }: { node: any }) => {
        return <CenterContentBlock {...node.fields}></CenterContentBlock>;
      },
      cityPlanner: ({ node }: { node: any }) => {
        return <CityPlannerBlock {...node.fields}></CityPlannerBlock>;
      },
    },
    link: ({ node }: { node: any }) => {
      return <LinkBlock node={node} />;
    },
    relationship: ({ node }: { node: any }) => {
      return <p>{JSON.stringify(node.value)}</p>;
    },
    ...rest,
  };
};

type Props = {
  data: SerializedEditorState;
  useBlogStyling?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

export default function LexicalRenderer(props: Props) {
  const { className, ...rest } = props;
  const useBlogStyling = props.useBlogStyling ?? true;
  return (
    <>
      <RehypePrettyCodeCopy />

      <RichText
        converters={jsxConverters}
        className={clsx(className, useBlogStyling ? 'bodyContent' : '')}
        {...rest}
      />
    </>
  );
}
